import React, { useState, useEffect, useRef } from 'react';
import { firmAPI, membersAPI, signatoriesAPI } from './api';

/* ── Small reusable pieces ─────────────────── */
const Inp = ({ label, value, onChange, type = 'text', placeholder = '', disabled }) => (
  <div className="s-fg">
    <label className="s-lbl">{label}</label>
    <input className="s-inp" type={type} value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder} disabled={disabled} />
  </div>
);

const ImgUpload = ({ label, value, onChange, hint }) => {
  const ref = useRef();
  const handle = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => onChange(ev.target.result);
    reader.readAsDataURL(file);
  };
  return (
    <div className="s-fg">
      <label className="s-lbl">{label}</label>
      <label className="s-img-upload" onClick={() => ref.current.click()}>
        <input ref={ref} type="file" accept="image/*" onChange={handle} style={{ display: 'none' }} />
        {value
          ? <img src={value} alt="preview" className="s-img-preview" />
          : <div className="s-img-placeholder">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span>{hint || 'Click to upload'}</span>
            </div>
        }
        {value && <span className="s-img-change">Click to change</span>}
      </label>
    </div>
  );
};

/* ── Main Settings component ───────────────── */
export default function Settings({ firm: initFirm, user, onClose, onFirmUpdated }) {
  const [tab, setTab] = useState('firm');

  /* Firm tab */
  const [firm, setFirm]       = useState({ ...initFirm });
  const [saving, setSaving]   = useState(false);
  const [firmMsg, setFirmMsg] = useState('');

  /* Members tab */
  const [members, setMembers]       = useState([]);
  const [newMember, setNewMember]   = useState({ name: '', email: '', password: '', role: 'member' });
  const [addingMember, setAddingMember] = useState(false);
  const [membersMsg, setMembersMsg] = useState('');

  /* Signatories tab */
  const [sigs, setSigs]         = useState([]);
  const [newSig, setNewSig]     = useState({ name: '', title: '', signature_data: '', is_default: false });
  const [addingSig, setAddingSig] = useState(false);
  const [sigsMsg, setSigsMsg]   = useState('');

  const setF = (k, v) => setFirm(p => ({ ...p, [k]: v }));
  const setNM = (k, v) => setNewMember(p => ({ ...p, [k]: v }));
  const setNS = (k, v) => setNewSig(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (tab === 'members') loadMembers();
    if (tab === 'signatories') loadSigs();
  }, [tab]); // eslint-disable-line

  const loadMembers = async () => {
    try { setMembers(await membersAPI.list()); } catch {}
  };

  const loadSigs = async () => {
    try { setSigs(await signatoriesAPI.list()); } catch {}
  };

  /* ── Save firm profile ── */
  const saveFirm = async (e) => {
    e.preventDefault(); setSaving(true); setFirmMsg('');
    try {
      const updated = await firmAPI.update({
        name:           firm.name,
        address:        firm.address,
        phone:          firm.phone,
        email:          firm.email,
        website:        firm.website,
        gst_number:     firm.gstNumber,
        pan:            firm.pan,
        cin:            firm.cin,
        bank_name:      firm.bankName,
        bank_account_name: firm.bankAccountName,
        bank_account:   firm.bankAccount,
        bank_ifsc:      firm.bankIfsc,
        bank_branch:    firm.bankBranch,
        logo_data:            firm.logoData,
        invoice_prefix:       firm.invoicePrefix,
        bank_balance_opening: firm.bankBalanceOpening !== undefined ? parseFloat(firm.bankBalanceOpening) : undefined,
      });
      setFirmMsg('Saved successfully');
      const mapped = {
        ...firm,
        gstNumber:           updated.gst_number,
        pan:                 updated.pan,
        cin:                 updated.cin,
        bankName:            updated.bank_name,
        bankAccountName:     updated.bank_account_name,
        bankAccount:         updated.bank_account,
        bankIfsc:            updated.bank_ifsc,
        bankBranch:          updated.bank_branch,
        logoData:            updated.logo_data,
        invoicePrefix:       updated.invoice_prefix,
        bankBalanceOpening:  updated.bank_balance_opening,
      };
      onFirmUpdated(mapped);
    } catch (err) { setFirmMsg(`Error: ${err.message}`); }
    finally { setSaving(false); }
  };

  /* ── Add member ── */
  const addMember = async (e) => {
    e.preventDefault(); setAddingMember(true); setMembersMsg('');
    try {
      const m = await membersAPI.add(newMember);
      setMembers(p => [...p, m]);
      setNewMember({ name: '', email: '', password: '', role: 'member' });
      setMembersMsg('Member added');
    } catch (err) { setMembersMsg(`Error: ${err.message}`); }
    finally { setAddingMember(false); }
  };

  const removeMember = async (id) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await membersAPI.remove(id);
      setMembers(p => p.filter(m => m.id !== id));
    } catch (err) { setMembersMsg(`Error: ${err.message}`); }
  };

  /* ── Add signatory ── */
  const addSig = async (e) => {
    e.preventDefault(); setAddingSig(true); setSigsMsg('');
    try {
      const s = await signatoriesAPI.add(newSig);
      if (newSig.is_default) setSigs(p => p.map(x => ({ ...x, is_default: false })));
      setSigs(p => [...p, s]);
      setNewSig({ name: '', title: '', signature_data: '', is_default: false });
      setSigsMsg('Signatory added');
    } catch (err) { setSigsMsg(`Error: ${err.message}`); }
    finally { setAddingSig(false); }
  };

  const setDefaultSig = async (id) => {
    try {
      await signatoriesAPI.setDefault(id);
      setSigs(p => p.map(s => ({ ...s, is_default: s.id === id })));
    } catch (err) { setSigsMsg(`Error: ${err.message}`); }
  };

  const removeSig = async (id) => {
    if (!window.confirm('Remove this signatory?')) return;
    try {
      await signatoriesAPI.remove(id);
      setSigs(p => p.filter(s => s.id !== id));
    } catch (err) { setSigsMsg(`Error: ${err.message}`); }
  };

  return (
    <div className="settings-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="settings-panel">
        {/* Header */}
        <div className="settings-header">
          <div>
            <h2 className="settings-title">Settings</h2>
            <p className="settings-sub">{firm.name}</p>
          </div>
          <button className="settings-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="settings-tabs">
          {[['firm','Firm Profile'],['members','Team Members'],['signatories','Signatories']].map(([k, label]) => (
            <button key={k} className={`settings-tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </div>

        <div className="settings-body">

          {/* ── Firm Profile ── */}
          {tab === 'firm' && (
            <form onSubmit={saveFirm} className="s-section">
              <div className="s-row-logo">
                <ImgUpload label="Company Logo" value={firm.logoData}
                  onChange={v => setF('logoData', v)}
                  hint="Upload PNG / SVG logo" />
                <div className="s-col">
                  <Inp label="Company Name"     value={firm.name}           onChange={v => setF('name', v)} />
                  <Inp label="Invoice Prefix"   value={firm.invoicePrefix}  onChange={v => setF('invoicePrefix', v)} placeholder="e.g. TI" />
                </div>
              </div>

              <div className="s-divider">Contact & Address</div>
              <div className="s-grid2">
                <Inp label="Official Email" value={firm.email}   onChange={v => setF('email', v)}   type="email" />
                <Inp label="Phone"          value={firm.phone}   onChange={v => setF('phone', v)} />
                <Inp label="Website"        value={firm.website} onChange={v => setF('website', v)} placeholder="https://..." />
              </div>
              <Inp label="Address" value={firm.address} onChange={v => setF('address', v)} />

              <div className="s-divider">Tax Registration</div>
              <div className="s-grid3">
                <Inp label="GST Number" value={firm.gstNumber} onChange={v => setF('gstNumber', v)} placeholder="06AAJCT2216R1ZX" />
                <Inp label="PAN"        value={firm.pan}       onChange={v => setF('pan', v)}        placeholder="AAJCT2216R" />
                <Inp label="CIN"        value={firm.cin}       onChange={v => setF('cin', v)}        placeholder="U74999HR2022PTC101756" />
              </div>

              <div className="s-divider">Bank Details</div>
              <div className="s-grid2">
                <Inp label="Account Name"            value={firm.bankAccountName} onChange={v => setF('bankAccountName', v)} placeholder="Triyog Innovations Private Limited" />
                <Inp label="Current Account No."     value={firm.bankAccount}     onChange={v => setF('bankAccount', v)} placeholder="252605000611" />
                <Inp label="IFSC Code"               value={firm.bankIfsc}        onChange={v => setF('bankIfsc', v)} placeholder="ICIC0000302" />
                <Inp label="Name of Bank and Address" value={firm.bankBranch}     onChange={v => setF('bankBranch', v)} placeholder="ICICI Bank Limited, Sector-15, Sonepat, Haryana-131001, India" />
                <Inp label="Bank Name"               value={firm.bankName}        onChange={v => setF('bankName', v)} placeholder="ICICI Bank Limited" />
              </div>
              <div className="s-divider">Opening Bank Balance</div>
              <div className="s-grid2">
                <Inp label="Opening Balance (₹)" value={firm.bankBalanceOpening} type="number"
                  onChange={v => setF('bankBalanceOpening', v)}
                  placeholder="Current balance before using this system" />
              </div>
              <p className="s-opening-hint">
                Set this once to your current bank balance. The dashboard will track credits (invoice payments) and debits (expenses) from here.
              </p>

              {firmMsg && <div className={`s-msg ${firmMsg.startsWith('Error') ? 'error' : 'ok'}`}>{firmMsg}</div>}
              <button className="s-save-btn" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save Firm Profile'}
              </button>
            </form>
          )}

          {/* ── Team Members ── */}
          {tab === 'members' && (
            <div className="s-section">
              {/* Members list */}
              <div className="s-list">
                {members.map(m => (
                  <div key={m.id} className="s-list-row">
                    <div className="s-member-avatar">{m.name.charAt(0).toUpperCase()}</div>
                    <div className="s-member-info">
                      <div className="s-member-name">{m.name}</div>
                      <div className="s-member-email">{m.email}</div>
                    </div>
                    <span className={`s-role-badge ${m.role}`}>{m.role}</span>
                    {!m.is_active && <span className="s-role-badge inactive">inactive</span>}
                    {m.id !== user.id && user.role === 'admin' && (
                      <button className="s-remove-btn" onClick={() => removeMember(m.id)} title="Remove">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
                          <path d="M9 6V4h6v2"/>
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                {members.length === 0 && <p className="s-empty">No team members yet.</p>}
              </div>

              {/* Add member form — admin only */}
              {user.role === 'admin' && (
                <form onSubmit={addMember} className="s-add-form">
                  <div className="s-divider">Add Team Member</div>
                  <div className="s-grid2">
                    <Inp label="Full Name" value={newMember.name} onChange={v => setNM('name', v)} />
                    <Inp label="Email"     value={newMember.email} onChange={v => setNM('email', v)} type="email" />
                    <Inp label="Password"  value={newMember.password} onChange={v => setNM('password', v)} type="password" placeholder="Temporary password" />
                    <div className="s-fg">
                      <label className="s-lbl">Role</label>
                      <select className="s-inp s-select" value={newMember.role} onChange={e => setNM('role', e.target.value)}>
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  {membersMsg && <div className={`s-msg ${membersMsg.startsWith('Error') ? 'error' : 'ok'}`}>{membersMsg}</div>}
                  <button className="s-add-btn" type="submit" disabled={addingMember}>
                    {addingMember ? 'Adding…' : '+ Add Member'}
                  </button>
                </form>
              )}
            </div>
          )}

          {/* ── Signatories ── */}
          {tab === 'signatories' && (
            <div className="s-section">
              {/* Signatories list */}
              <div className="s-list">
                {sigs.map(s => (
                  <div key={s.id} className="s-list-row s-sig-row">
                    <div className="s-sig-img-wrap">
                      {s.signature_data
                        ? <img src={s.signature_data} alt="sig" className="s-sig-thumb" />
                        : <div className="s-sig-no-img">No sig</div>
                      }
                    </div>
                    <div className="s-member-info">
                      <div className="s-member-name">{s.name}</div>
                      <div className="s-member-email">{s.title || '—'}</div>
                    </div>
                    {s.is_default && <span className="s-role-badge default-sig">Default</span>}
                    <div className="s-sig-actions">
                      {!s.is_default && (
                        <button className="s-setdefault-btn" onClick={() => setDefaultSig(s.id)}>
                          Set Default
                        </button>
                      )}
                      <button className="s-remove-btn" onClick={() => removeSig(s.id)} title="Remove">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14H6L5 6"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
                {sigs.length === 0 && <p className="s-empty">No signatories added yet.</p>}
              </div>

              {/* Add signatory form */}
              <form onSubmit={addSig} className="s-add-form">
                <div className="s-divider">Add Authorised Signatory</div>
                <div className="s-grid2">
                  <Inp label="Full Name"    value={newSig.name}  onChange={v => setNS('name', v)} />
                  <Inp label="Designation" value={newSig.title} onChange={v => setNS('title', v)} placeholder="e.g. Director" />
                </div>
                <ImgUpload label="Signature Image"
                  value={newSig.signature_data}
                  onChange={v => setNS('signature_data', v)}
                  hint="Upload PNG / JPG signature" />
                <label className="s-checkbox-label">
                  <input type="checkbox" checked={newSig.is_default}
                    onChange={e => setNS('is_default', e.target.checked)} />
                  Set as default signatory
                </label>
                {sigsMsg && <div className={`s-msg ${sigsMsg.startsWith('Error') ? 'error' : 'ok'}`}>{sigsMsg}</div>}
                <button className="s-add-btn" type="submit" disabled={addingSig || !newSig.name}>
                  {addingSig ? 'Adding…' : '+ Add Signatory'}
                </button>
              </form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
